import React from 'react';
import { X, FileText, Shield, AlertTriangle, CheckCircle, Mail, Phone } from 'lucide-react';

interface TermsOfServicePageProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsOfServicePage: React.FC<TermsOfServicePageProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95 backdrop-blur-lg">
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl border border-gray-600/50 shadow-2xl shadow-purple-600/30 animate-fade-in-up overflow-y-auto">
        {/* Gradient Border Effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/30 via-blue-500/30 to-purple-400/30 rounded-3xl blur-lg -z-10"></div>
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 md:p-8 border-b border-gray-700/50 sticky top-0 bg-gray-900/90 backdrop-blur-sm rounded-t-3xl">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-robotic mb-2">Terms of Service</h2>
            <p className="text-gray-400 font-tech text-sm md:text-base">Legal terms and conditions for using our services</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-red-500/20 hover:border-red-500/30 border border-gray-700/50 transition-all duration-300 hover:scale-110 flex-shrink-0"
          >
            <X className="w-5 h-5 text-gray-400 hover:text-red-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 space-y-8">
          {/* Last Updated */}
          <div className="text-center p-4 bg-emerald-500/10 rounded-xl border border-emerald-400/20">
            <p className="text-emerald-400 font-tech">Last updated: January 2025</p>
          </div>

          {/* Introduction */}
          <div>
            <div className="flex items-center mb-4">
              <FileText className="w-6 h-6 text-emerald-400 mr-3" />
              <h3 className="text-2xl font-bold text-white font-robotic">Agreement to Terms</h3>
            </div>
            <p className="text-gray-300 leading-relaxed font-tech">
              By accessing and using DM.AI 4u's services, you agree to be bound by these Terms of Service and all applicable 
              laws and regulations. If you do not agree with any of these terms, you are prohibited from using our services.
            </p>
          </div>

          {/* Service Description */}
          <div>
            <div className="flex items-center mb-4">
              <Shield className="w-6 h-6 text-blue-400 mr-3" />
              <h3 className="text-2xl font-bold text-white font-robotic">Our Services</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800/50">
                <h4 className="text-lg font-bold text-white mb-2 font-robotic">AI Solutions</h4>
                <ul className="text-gray-300 space-y-1 font-tech text-sm">
                  <li>• AI Customer Support systems</li>
                  <li>• Intelligent Chat Bot services</li>
                  <li>• CRM Integration solutions</li>
                  <li>• Smart Scheduling automation</li>
                  <li>• Website and Ad Campaign management</li>
                </ul>
              </div>
              <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800/50">
                <h4 className="text-lg font-bold text-white mb-2 font-robotic">Support Services</h4>
                <ul className="text-gray-300 space-y-1 font-tech text-sm">
                  <li>• Technical support and maintenance</li>
                  <li>• Training and onboarding</li>
                  <li>• Custom integrations</li>
                  <li>• Performance monitoring</li>
                  <li>• Regular updates and improvements</li>
                </ul>
              </div>
            </div>
          </div>

          {/* User Responsibilities */}
          <div>
            <div className="flex items-center mb-4">
              <CheckCircle className="w-6 h-6 text-purple-400 mr-3" />
              <h3 className="text-2xl font-bold text-white font-robotic">Your Responsibilities</h3>
            </div>
            <div className="p-6 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-xl border border-purple-400/20">
              <ul className="text-gray-300 space-y-2 font-tech">
                <li>• <strong className="text-white">Accurate Information:</strong> Provide truthful and accurate information</li>
                <li>• <strong className="text-white">Account Security:</strong> Maintain the confidentiality of your account credentials</li>
                <li>• <strong className="text-white">Lawful Use:</strong> Use our services only for lawful business purposes</li>
                <li>• <strong className="text-white">Compliance:</strong> Comply with all applicable laws and regulations</li>
                <li>• <strong className="text-white">Data Protection:</strong> Ensure you have rights to any data you provide</li>
              </ul>
            </div>
          </div>

          {/* Payment Terms */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4 font-robotic">Payment Terms</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-400/20">
                <h4 className="text-lg font-bold text-emerald-400 mb-2 font-robotic">Billing</h4>
                <ul className="text-gray-300 space-y-1 font-tech text-sm">
                  <li>• Monthly subscription billing</li>
                  <li>• Automatic renewal unless cancelled</li>
                  <li>• No setup fees or hidden charges</li>
                  <li>• 30-day money-back guarantee</li>
                </ul>
              </div>
              <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-400/20">
                <h4 className="text-lg font-bold text-blue-400 mb-2 font-robotic">Cancellation</h4>
                <ul className="text-gray-300 space-y-1 font-tech text-sm">
                  <li>• Cancel anytime with 30 days notice</li>
                  <li>• No cancellation fees</li>
                  <li>• Service continues until end of billing period</li>
                  <li>• Data export available upon request</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Service Availability */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4 font-robotic">Service Availability</h3>
            <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800/50">
              <p className="text-gray-300 leading-relaxed font-tech mb-3">
                We strive to maintain 99.9% uptime for our services. However, we do not guarantee uninterrupted service and 
                may perform maintenance that temporarily affects availability.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-tech">99.9% Uptime Target</span>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm font-tech">24/7 Monitoring</span>
                <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm font-tech">Scheduled Maintenance</span>
              </div>
            </div>
          </div>

          {/* Limitations */}
          <div>
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-yellow-400 mr-3" />
              <h3 className="text-2xl font-bold text-white font-robotic">Limitations of Liability</h3>
            </div>
            <div className="p-6 bg-gradient-to-r from-yellow-500/10 to-red-500/10 rounded-xl border border-yellow-400/20">
              <ul className="text-gray-300 space-y-2 font-tech">
                <li>• <strong className="text-white">Service Limitations:</strong> Our liability is limited to the amount paid for services</li>
                <li>• <strong className="text-white">Indirect Damages:</strong> We are not liable for indirect, incidental, or consequential damages</li>
                <li>• <strong className="text-white">Third-Party Services:</strong> We are not responsible for third-party integrations or services</li>
                <li>• <strong className="text-white">Data Loss:</strong> While we implement backups, you are responsible for your own data protection</li>
              </ul>
            </div>
          </div>

          {/* Intellectual Property */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4 font-robotic">Intellectual Property</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800/50">
                <h4 className="text-lg font-bold text-white mb-2 font-robotic">Our Rights</h4>
                <ul className="text-gray-300 space-y-1 font-tech text-sm">
                  <li>• DM.AI 4u owns all service technology</li>
                  <li>• Trademarks and branding are protected</li>
                  <li>• AI algorithms and software are proprietary</li>
                  <li>• Documentation and materials are copyrighted</li>
                </ul>
              </div>
              <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800/50">
                <h4 className="text-lg font-bold text-white mb-2 font-robotic">Your Rights</h4>
                <ul className="text-gray-300 space-y-1 font-tech text-sm">
                  <li>• You retain ownership of your data</li>
                  <li>• License to use our services as agreed</li>
                  <li>• Right to export your data</li>
                  <li>• Your content remains your property</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Termination */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4 font-robotic">Termination</h3>
            <div className="p-4 bg-red-500/10 rounded-xl border border-red-400/20">
              <p className="text-gray-300 leading-relaxed font-tech mb-3">
                Either party may terminate this agreement with 30 days written notice. We may terminate immediately for 
                breach of terms, non-payment, or illegal use of our services.
              </p>
              <ul className="text-gray-300 space-y-1 font-tech text-sm">
                <li>• 30 days notice for standard termination</li>
                <li>• Immediate termination for terms violation</li>
                <li>• Data export available for 90 days post-termination</li>
                <li>• Refunds processed according to our refund policy</li>
              </ul>
            </div>
          </div>

          {/* Governing Law */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4 font-robotic">Governing Law</h3>
            <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800/50">
              <p className="text-gray-300 leading-relaxed font-tech">
                These terms are governed by the laws of England and Wales. Any disputes will be resolved through the 
                courts of England and Wales or through binding arbitration as mutually agreed.
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="p-6 bg-gradient-to-r from-emerald-600/10 via-blue-500/10 to-purple-400/10 rounded-xl border border-gray-700/50">
            <h3 className="text-2xl font-bold text-white mb-4 font-robotic">Questions About These Terms?</h3>
            <p className="text-gray-300 mb-4 font-tech">
              If you have questions about these Terms of Service, please contact us:
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center text-gray-300">
                <Mail className="w-5 h-5 mr-2 text-emerald-400" />
                <span className="font-tech">dom@dm4u.ai</span>
              </div>
              <div className="flex items-center text-gray-300">
                <Phone className="w-5 h-5 mr-2 text-blue-400" />
                <a href="tel:+447508300353" className="font-tech hover:text-blue-400 transition-colors duration-300">07508 300353</a>
              </div>
            </div>
          </div>

          {/* Updates */}
          <div className="text-center p-4 bg-purple-500/10 rounded-xl border border-purple-400/20">
            <p className="text-purple-400 font-tech">
              We reserve the right to modify these terms at any time. Material changes will be communicated via email 
              with 30 days notice before taking effect.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;